import { WindowSpec } from '@cutr/constants/windows/windows';
import { SystemClass } from '@cutr/constants/windows/windows-configurations.ts';
import {
  getAreas,
  getFrameDimensions,
  getInnerBeams,
  SideWidths,
} from '@cutr/constants/windows/windows-geometry.ts';
import React from 'react';
import { Layer, Stage } from 'react-konva';

import { Areas } from './Areas';
import { BottomCaptions, LeftCaptions } from './DimensionCaptions';
import { Frame } from './frames/Frame';
import { KonvaProvider, useKonva } from './KonvaContext';
import { getOffsets, RenderStyles } from './renderStyles';

type WindowReadOnlyProps = {
  windowSpec: WindowSpec;
  positionId?: string;
  renderStyles: RenderStyles;
  frameBeamWidthsMM?: SideWidths;
  systemClass?: SystemClass;
};

export const WindowReadOnly = ({
  windowSpec,
  positionId,
  renderStyles,
  frameBeamWidthsMM,
  systemClass,
}: WindowReadOnlyProps) => {
  const containerId = React.useId();

  return (
    <KonvaProvider
      containerId={containerId}
      key={`${containerId}-konvaProvider`}
      stageScaleX={0}
      stageScaleY={0}
      stageWidth={0}
      stageHeight={0}
      renderStyles={renderStyles}
      positionId={positionId}
    >
      <WindowReadOnlyContent
        windowSpec={windowSpec}
        frameBeamWidthsMM={frameBeamWidthsMM}
        systemClass={systemClass}
      />
    </KonvaProvider>
  );
};

const WindowReadOnlyContent = ({
  windowSpec,
  frameBeamWidthsMM,
  systemClass,
}: {
  windowSpec: WindowSpec;
  frameBeamWidthsMM?: SideWidths;
  systemClass?: SystemClass;
}) => {
  const {
    stageRef,
    setStageDimensions,
    containerId,
    stageScaleX,
    stageScaleY,
    stageWidth,
    stageHeight,
    renderStyles,
  } = useKonva();

  const { topMM, bottomMM, leftMM } = getOffsets(renderStyles);
  const { frameWidthMM, frameHeightMM } = getFrameDimensions(
    windowSpec.frameShape
  );

  React.useLayoutEffect(() => {
    const container = document.querySelector<HTMLDivElement>(`#${containerId}`);
    if (!container) return;

    const parent = container.parentElement;
    if (!parent) return;

    const parentHeight = parent.clientHeight;
    const parentWidth = parent.clientWidth;

    const updateStageDimensions = (
      newParentWidth: number,
      newParentHeight: number
    ) => {
      const { topMM, bottomMM, leftMM, rightMM } = getOffsets(renderStyles);
      const { frameHeightMM } = getFrameDimensions(windowSpec.frameShape);
      const totalHeight = frameHeightMM + topMM + bottomMM;
      const totalWidth = frameWidthMM + leftMM + rightMM;

      const stageScaleY = newParentHeight / totalHeight;
      const stageScaleX = newParentWidth / totalWidth;

      const scale = Math.min(stageScaleX, stageScaleY);

      setStageDimensions({
        stageWidth: newParentWidth,
        stageHeight: newParentHeight,
        stageScaleX: scale,
        stageScaleY: scale,
      });
    };

    updateStageDimensions(parentWidth, parentHeight);
  }, [
    containerId,
    frameWidthMM,
    windowSpec.frameShape,
    setStageDimensions,
    renderStyles,
  ]);

  /**
   * Maps frame dimensions to a bounding box with maximum 600 in length or width
   * while maintaining aspect ratio, including offsets for padding and dimension lines
   * @param height - Frame height in millimeters
   * @param width - Frame width in millimeters
   * @param offsets - Object containing leftMM, rightMM, topMM, bottomMM offsets
   * @param maxSize - Maximum size for the bounding box (default: 600)
   * @returns Object with containerWidth and containerHeight
   */
  const getBoundingBoxDimensions = (
    height: number,
    width: number,
    offsets: {
      leftMM: number;
      rightMM: number;
      topMM: number;
      bottomMM: number;
    },
    maxSize: number = 600
  ) => {
    // Calculate total dimensions including offsets
    const totalHeight = height + offsets.topMM + offsets.bottomMM;
    const totalWidth = width + offsets.leftMM + offsets.rightMM;

    if (totalHeight <= maxSize && totalWidth <= maxSize) {
      // Both dimensions fit within maxSize, return total dimensions
      return { containerWidth: totalWidth, containerHeight: totalHeight };
    }

    // Calculate scale factor to fit within maxSize while maintaining aspect ratio
    const scaleFactor = Math.min(maxSize / totalHeight, maxSize / totalWidth);

    return {
      containerWidth: Math.round(totalWidth * scaleFactor),
      containerHeight: Math.round(totalHeight * scaleFactor),
    };
  };

  const { containerWidth, containerHeight } = getBoundingBoxDimensions(
    frameHeightMM,
    frameWidthMM,
    getOffsets(renderStyles)
  );

  return (
    <div
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        display: 'flex',
      }}
    >
      <Stage
        id={containerId}
        scaleY={-stageScaleY}
        scaleX={stageScaleX}
        width={stageWidth}
        height={stageHeight}
        offsetY={frameHeightMM + bottomMM + topMM}
        ref={stageRef}
      >
        <Layer listening={false}>
          <Frame
            frame={windowSpec.frameShape}
            x={leftMM}
            y={bottomMM}
            innerBeams={getInnerBeams(windowSpec)}
            frameBeamWidthsMM={frameBeamWidthsMM}
          />
          {renderStyles.dimensionGuidelines !== 'none' && stageHeight > 0 && (
            <>
              <LeftCaptions
                frameHeightMM={frameHeightMM}
                frameY={bottomMM}
                windowSpec={windowSpec}
              />
              <BottomCaptions
                frameWidthMM={frameWidthMM}
                frameX={leftMM}
                windowSpec={windowSpec}
              />
            </>
          )}
          <Areas
            windowSpec={windowSpec}
            areas={getAreas(windowSpec)}
            x={leftMM}
            y={bottomMM}
            areaConfigurations={windowSpec.areaConfigurations}
            frameBeamWidthsMM={frameBeamWidthsMM}
            systemClass={systemClass}
          />
        </Layer>
      </Stage>
    </div>
  );
};
